import React, { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { uploadProfilePhoto } from "@/services/student/profile.service";
import { getProfilePhotoUrl } from "@/lib/avatar";

export interface ProfilePhotoUploadProps {
  value?: string | null;
  onChange?: (value: string | null) => void;
  disabled?: boolean;
  className?: string;
}

export const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  value,
  onChange,
  disabled = false,
  className,
}) => {
  const [previewUrl, setPreviewUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const url = getProfilePhotoUrl(value);
    setPreviewUrl(url);
  }, [value]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setIsLoading(true);
    setUploadProgress(0);

    try {
      // Validate file type
      const permittedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
      if (!permittedTypes.includes(file.type)) {
        throw new Error("Tipo de arquivo não permitido. Use: jpg, jpeg, png, gif ou webp.");
      }

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error("Arquivo muito grande. Tamanho máximo: 5MB.");
      }

      const response = await uploadProfilePhoto(file);
      
      if (onChange) {
        onChange(response.photoFilename);
      }
    } catch (err: any) {
      setError(err.message || "Erro ao fazer upload da foto. Tente novamente.");
      console.error(err);
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemovePhoto = () => {
    if (onChange) {
      onChange(null);
    }
    setPreviewUrl("");
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerFileSelect = () => {
    if (!disabled && !isLoading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={cn("w-full space-y-2", className)}>
      <Card className="p-4">
        <div className="flex items-center gap-4">
          <div
            className={cn(
              "w-24 h-24 rounded-full flex items-center justify-center bg-neutral-100 border border-dashed overflow-hidden",
              { "cursor-pointer hover:bg-neutral-200": !disabled && !isLoading }
            )}
            onClick={triggerFileSelect}
          >
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Foto de perfil"
                className="w-full h-full object-cover"
                onError={() => {
                  setError("Erro ao carregar imagem");
                  setPreviewUrl("");
                }}
              />
            ) : (
              <ImageIcon className="h-8 w-8 text-neutral-400" />
            )}
          </div>
          <div className="flex-1 space-y-2">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={triggerFileSelect}
                disabled={disabled || isLoading}
                size="sm"
              >
                <Upload className="h-4 w-4 mr-2" />
                {isLoading ? "Enviando..." : "Escolher foto"}
              </Button>
              {value && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleRemovePhoto}
                  disabled={disabled || isLoading}
                >
                  <X className="h-4 w-4 mr-2" />
                  Remover
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              JPG, PNG, GIF ou WEBP. Máximo 5MB.
            </p>
          </div>
        </div>

        {isLoading && (
          <div className="mt-2 space-y-1">
            <div className="flex justify-between text-xs text-gray-600">
              <span>Enviando...</span>
              <span>{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} className="w-full" />
          </div>
        )}

        {error && (
          <div className="mt-2 text-sm text-destructive bg-destructive/10 p-2 rounded-md">
            {error}
          </div>
        )}

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          accept="image/png, image/jpeg, image/gif, image/webp"
          disabled={disabled || isLoading}
          aria-label="Upload foto de perfil"
        />
      </Card>
    </div>
  );
};
